import { useCallback, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import useLocalStorage from '@/hooks/useLocalStorage';
import { selectAccountSlug } from '@/redux/session/selectors';

import { findBestPageMatch } from '../pageRegistry';
import { type RecentPage } from '../types';
import { pickLongestMatch } from '../utils';

const MAX_RECENT = 5;

type TrackPageInput = Omit<RecentPage, 'timestamp'>;

type UseRecentPagesReturn = {
  recentPages: RecentPage[];
  trackPage: (page: TrackPageInput) => void;
};

/** Persists and retrieves the last 5 visited pages per account in localStorage. */
export const useRecentPages = (): UseRecentPagesReturn => {
  const accountSlug = useSelector(selectAccountSlug);
  const storageKey = accountSlug ? `commandPalette_recent_${accountSlug}` : undefined;
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const [recentPages, setRecentPages] = useLocalStorage<RecentPage[]>(storageKey, []);

  const trackPage = useCallback(
    (page: TrackPageInput) => {
      setRecentPages((prev) => {
        const filtered = prev.filter((p) => p.path !== page.path);
        return [{ ...page, timestamp: Date.now() }, ...filtered].slice(0, MAX_RECENT);
      });
    },
    [setRecentPages]
  );

  // Auto-track route changes
  useEffect(() => {
    if (!accountSlug) {
      return;
    }

    const prefix = `/${accountSlug}`;
    const relativePath = pathname.startsWith(prefix) ? pathname.slice(prefix.length) || '/' : null;

    // Try fullPath entries against the absolute pathname first
    const fullPathMatch = findBestPageMatch(pathname, (e) => !!e.fullPath);

    // Then try relative entries against the slug-stripped path
    const relativeMatch = relativePath ? findBestPageMatch(relativePath) : null;

    // Pick whichever matched with the longer path (more specific)
    const bestEntry = pickLongestMatch(fullPathMatch, relativeMatch);

    if (!bestEntry) {
      return;
    }

    trackPage({ path: bestEntry.path, label: t(bestEntry.i18nKey) });
  }, [pathname, accountSlug, trackPage, t]);

  return { recentPages, trackPage };
};
